import './PlayerInformationTab.scss';

import { Button, Checkbox, Select } from '@trackman/web-shared-components';
import { Trans, useTranslation } from 'react-i18next';

import ErrorInfo from 'components/ErrorInfo/ErrorInfo';
import { Input } from '@trackman/web-shared-components';
import { ReactComponent as MapMarkerIcon } from 'images/icons/MapMarker.svg';
import { ReactComponent as ProfileIcon } from 'images/icons/Profile.svg';
import Separator from 'components/Separator/Separator';
import { observer } from 'mobx-react-lite';
import { trimTooManySpaces } from '../../../../utilities/inputValidation';
import { useState } from 'react';
import { useStores } from 'index';

export const PlayerInformationTab = observer(() => {
  const ps = useStores().profileStore;
  const { t } = useTranslation();
  const [isInfoValid, setIsInfoValid] = useState(true);

  return (
    <>
      <div className='profile-section personal-info-section'>
        <div className='profile-inputs-column'>
          <div className='profile-inputs-column-title'>
            <ProfileIcon />
            {t('PersonalInformation', { ns: 'editProfile' })}
          </div>
          <Input
            label={t('PlayerName', { ns: 'editProfile' })}
            value={ps.profileData.firstName ?? ''}
            required
            error={ps.isLoaded && !ps.profileData.firstName}
            hint={ps.isLoaded && !ps.profileData.firstName ? <ErrorInfo /> : ' '}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              ps.profileData.firstName = trimTooManySpaces(e.currentTarget.value);
              if (!e.currentTarget.value.replace(/\s/g, '').length) ps.profileData.firstName = '';
              setIsInfoValid(!!ps.profileData.firstName);
            }}
          />
          <Input
            label={t('PlayerSurname', { ns: 'editProfile' })}
            value={ps.profileData.lastName ?? ''}
            required
            error={ps.isLoaded && !ps.profileData.lastName}
            hint={ps.isLoaded && !ps.profileData.lastName ? <ErrorInfo /> : ' '}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              ps.profileData.lastName = trimTooManySpaces(e.currentTarget.value);
              if (!e.currentTarget.value.replace(/\s/g, '').length) ps.profileData.lastName = '';
              setIsInfoValid(!!ps.profileData.lastName);
            }}
          />
          <Select
            label={t('Gender', { ns: 'editProfile' })}
            value={ps.profileData.gender ?? ''}
            hint=' '
            onChange={(event) => (ps.profileData.gender = event.target.value as string)}
            selectOptions={[
              { label: t('Male', { ns: 'editProfile' }), value: 'male' },
              { label: t('Female', { ns: 'editProfile' }), value: 'female' },
            ]}
          />
          <Input
            label={t('MobilePhone', { ns: 'editProfile' })}
            value={ps.profileData.phone ?? ''}
            hint=' '
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              ps.profileData.phone = trimTooManySpaces(e.currentTarget.value);
              if (!e.currentTarget.value.replace(/\s/g, '').length) ps.profileData.phone = '';
              setIsInfoValid(!!ps.profileData.phone);
            }}
          />
          <div className='newsletter-checkbox'>
            <Checkbox
              onChange={() => (ps.profileData.acceptedEmails = !ps.profileData.acceptedEmails)}
              checked={ps.profileData.acceptedEmails}
              label={
                <p>
                  <Trans i18nKey='ReceiveOffersAndPromotionsFromTrackman' ns='editProfile'>
                    Receive offers and promotions from Trackman&nbsp;
                    <span>(CVR.DK27352677)</span>
                  </Trans>
                </p>
              }
            />
          </div>
        </div>
        <div className='profile-inputs-column'>
          <div className='profile-inputs-column-title'>
            <MapMarkerIcon />
            {t('AddressInformation', { ns: 'editProfile' })}
          </div>
          <Input
            label={t('StreetName1', { ns: 'editProfile' })}
            value={ps.profileData.address.street1 ?? ''}
            hint=' '
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              ps.profileData.address.street1 = trimTooManySpaces(e.currentTarget.value);
            }}
          />
          <Input
            label={t('StreetName2', { ns: 'editProfile' })}
            value={ps.profileData.address.street2 ?? ''}
            hint=' '
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              ps.profileData.address.street2 = trimTooManySpaces(e.currentTarget.value);
            }}
          />
          <Input
            label={t('PostalCode', { ns: 'editProfile' })}
            value={ps.profileData.address.postalCode ?? ''}
            hint=' '
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              ps.profileData.address.postalCode = trimTooManySpaces(e.currentTarget.value);
            }}
          />
          <Input
            label={t('City', { ns: 'editProfile' })}
            value={ps.profileData.address.city ?? ''}
            hint=' '
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              ps.profileData.address.city = trimTooManySpaces(e.currentTarget.value);
            }}
          />
          <Select
            label={t('Country', { ns: 'editProfile' })}
            value={ps.profileData.address.country ?? ''}
            hint=' '
            required={true}
            error={ps.isLoaded && !ps.profileData.address.country}
            onChange={(event) => {
              ps.profileData.address.country = event.target.value as string;
              setIsInfoValid(!!ps.profileData.address.country.trim());
            }}
            selectOptions={ps.signUpLists.Countries.map((country) => {
              return {
                label: country.name === ' ' ? t('PleaseSelectACountry', { ns: 'editProfile' }) : country.name,
                value: country.name,
              };
            })}
          />
          {ps.profileData.address.country === 'United States' && (
            <Select
              label={t('State', { ns: 'editProfile' })}
              value={ps.profileData.address.state ?? ' ' ?? ''}
              hint=' '
              onChange={(event) => {
                ps.profileData.address.state = event.target.value as string;
              }}
              selectOptions={ps.signUpLists.States.map((state) => {
                return {
                  label: state === ' ' ? t('PleaseSelectAState', { ns: 'editProfile' }) : state,
                  value: state,
                };
              })}
            />
          )}
          <div className='button-wrapper'>
            <Button
              label={t('SaveChanges', { ns: 'editProfile' })}
              onClick={() => {
                ps.saveProfile();
              }}
              intent='primary'
              isLoading={ps.isSavingProfile}
              disabled={ps.isLoading || ps.isSavingProfile || !isInfoValid}
              className='styled-button primary'
            />
            {!isInfoValid ? <ErrorInfo errorMsg={t('ProfileInformationIsMissing', { ns: 'editProfile' })} position='initial' /> : null}
          </div>
        </div>
      </div>
      <Separator />
    </>
  );
});

export default PlayerInformationTab;
